import { CSSProperties, useEffect, useRef, useState } from 'react';
import type { DockPayload } from '../../app/shell-context';
import { Icon } from '../../components/Icon';
import { AgentConversation } from './AgentConversation';

interface ContextDockProps {
  open: boolean;
  payload: DockPayload;
  onClose: () => void;
}

export function ContextDock({ open, payload, onClose }: ContextDockProps) {
  const [agentMounted, setAgentMounted] = useState(open && payload.kind === 'agent');
  const [width, setWidth] = useState(() => {
    try { return Math.min(720, Math.max(420, Number(window.localStorage.getItem('caigen.context-dock.width')) || 480)); }
    catch { return 480; }
  });
  const dragRef = useRef<{ startX: number; startWidth: number }>();
  const widthRef = useRef(width);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [open, onClose]);

  useEffect(() => {
    if (open && payload.kind === 'agent') setAgentMounted(true);
  }, [open, payload.kind]);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      if (!dragRef.current) return;
      const next = Math.min(720, Math.max(420, dragRef.current.startWidth + dragRef.current.startX - event.clientX));
      widthRef.current = next;
      setWidth(next);
    };
    const stop = () => {
      if (!dragRef.current) return;
      dragRef.current = undefined;
      document.body.classList.remove('is-resizing-dock');
      try { window.localStorage.setItem('caigen.context-dock.width', String(widthRef.current)); } catch { /* optional preference */ }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      document.body.classList.remove('is-resizing-dock');
    };
  }, []);

  const notice = payload.kind === 'evidence' ? payload.notice : undefined;

  return <>
    <button className={`dock-backdrop${open ? ' is-open' : ''}`} onClick={onClose} aria-label="关闭上下文面板" tabIndex={open ? 0 : -1} />
    <aside className={`context-dock${open ? ' is-open' : ''}`} style={{ '--dock-width': `${width}px` } as CSSProperties} aria-hidden={!open} aria-label="菜根上下文面板">
      <button className="dock-resize-handle" type="button" aria-label="拖动调整助手面板宽度" onPointerDown={(event) => { dragRef.current = { startX: event.clientX, startWidth: width }; document.body.classList.add('is-resizing-dock'); }} />
      <header className="dock-header">
        <div><small>{payload.kind === 'agent' ? 'CAIGEN · AGENT' : 'CONTEXT · EVIDENCE'}</small><h2>{payload.kind === 'agent' ? '问问菜根' : '来源与行动'}</h2></div>
        <button type="button" onClick={onClose} aria-label="关闭"><Icon name="close" /></button>
      </header>
      {payload.kind === 'agent'
        ? agentMounted ? <AgentConversation initialPrompt={payload.prompt} surface="context-dock" /> : null
        : <div className="evidence-view">
            <div className="evidence-source"><span>{(notice?.source || '信').slice(0, 1)}</span><p><b>{notice?.source || '尚未选择通知'}</b><small>{notice?.createdAt?.slice(0, 16).replace('T', ' ') || '等待来源证据'}</small></p></div>
            <article>
              <em>Notification / Evidence</em>
              <h3>{notice?.title || '从首页选择一条通知查看详情'}</h3>
              <p>{notice?.summary || notice?.raw || '这里仅展示来源、提取字段和待确认行动，不作为常驻信息栏。'}</p>
              <dl>
                <div><dt>开始</dt><dd>{[notice?.date, notice?.time].filter(Boolean).join(' ') || '—'}</dd></div>
                <div><dt>截止</dt><dd>{notice?.due || '—'}</dd></div>
                <div><dt>来源</dt><dd>{notice?.source || '—'}</dd></div>
              </dl>
            </article>
            <div className="evidence-actions">
              <a href="/calendar">进入日历确认</a>
              {notice?.sourceUrl ? <a href={notice.sourceUrl} target="_blank" rel="noreferrer">查看来源</a> : <button disabled>暂无来源链接</button>}
            </div>
            <p className="agent-boundary">所有行动均需确认；ContextDock 不会直接修改业务数据。</p>
          </div>}
    </aside>
  </>;
}
